import { cn } from '@utazzo/ui';
import { Check, Pencil, Search, UserRound, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useDestinationChangeAssigneeOptions } from '../use-leads';

/**
 * "Assigned agent" on the approve step of a DESTINATION_CHANGE request.
 *
 * A new destination often belongs to a different agent's coverage, so the
 * reviewer can hand the lead over in the same step as the approval instead of
 * approving first and reassigning after (which, for a team lead, could not
 * reach another team at all).
 *
 * The options are the request's own (`.../assignee-options`), server-scoped:
 * ADMIN sees every sales user; a SALES_LEAD sees their team PLUS the other team
 * leads - the one place a team lead reaches past their own team, and only while
 * a destination change is pending. Each option says whether they cover the
 * requested destination; the ones who do not are listed but cannot be picked,
 * because the approval would be refused for them.
 *
 * Inline rather than a popover: it lives inside a dialog, whose scroll box would
 * clip an absolutely-positioned panel. Plain text until the pencil is clicked,
 * like the owner control on the lead header.
 */
export const DestinationChangeAssigneePicker = ({
  leadId,
  requestId,
  destinationName,
  value,
  onChange,
  disabled,
}: {
  leadId: string;
  requestId: string;
  /** The destination being approved, for the coverage wording. */
  destinationName: string;
  /** The picked agent, or null to leave the owner as they are. */
  value: string | null;
  onChange: (agentId: string | null) => void;
  disabled?: boolean;
}): React.JSX.Element => {
  const options = useDestinationChangeAssigneeOptions(leadId, requestId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => options.data?.data ?? [], [options.data]);
  const currentAgentId = options.data?.currentAgentId ?? null;
  const current = rows.find((row) => row.id === currentAgentId) ?? null;
  const picked = rows.find((row) => row.id === value) ?? null;

  /** Name match, with the agents who can actually take the lead on top. */
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((row) => !q || row.fullName.toLowerCase().includes(q))
      .sort(
        (a, b) =>
          Number(b.coversDestination) - Number(a.coversDestination) ||
          a.fullName.localeCompare(b.fullName),
      );
  }, [rows, query]);

  const pick = (agentId: string): void => {
    // Picking the current owner again is "no change", not a reassignment.
    onChange(agentId === currentAgentId ? null : agentId);
    setOpen(false);
  };
  const toggle = (): void => {
    setQuery('');
    setOpen((state) => !state);
  };

  // The owner is only known to be a problem when they are in the list with the
  // flag off. An owner outside the reviewer's options is simply unknown here.
  const ownerDoesNotCover = picked === null && current !== null && !current.coversDestination;

  return (
    <div className="space-y-1.5">
      <p className="text-foreground text-sm font-medium">Assigned agent</p>
      <div className="border-border flex h-9 items-center gap-2 rounded-md border pr-1 pl-2.5">
        <UserRound className="text-muted-foreground h-4 w-4 shrink-0" />
        <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
          {options.isLoading
            ? 'Loading…'
            : picked
              ? picked.fullName
              : (current?.fullName ?? (currentAgentId ? 'Current owner' : 'Unassigned'))}
        </span>
        {picked ? (
          <span className="bg-primary/10 text-primary shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium">
            Will be reassigned
          </span>
        ) : (
          <span className="text-muted-foreground shrink-0 text-xs">No change</span>
        )}
        {picked && (
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label="Keep the current owner"
            title="Keep the current owner"
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={toggle}
          disabled={disabled || options.isLoading || options.isError}
          aria-expanded={open}
          aria-label="Change assigned agent"
          title="Hand this lead to another agent with the approval"
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>

      {ownerDoesNotCover && (
        <p className="border-warning/40 bg-warning/10 text-foreground rounded-md border px-2.5 py-1.5 text-xs">
          {current.fullName} does not cover {destinationName}. Pick an agent who does, or the lead
          stays with them after approval.
        </p>
      )}
      {options.isError && (
        <p className="text-muted-foreground text-xs">
          Could not load the agents. You can still approve - the owner stays as they are.
        </p>
      )}

      {open && (
        <div
          className="border-border rounded-md border p-1"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              // Close the list, not the whole dialog around it.
              event.stopPropagation();
              setOpen(false);
            }
          }}
        >
          <div className="border-border mb-1 flex items-center gap-1.5 border-b px-2 pb-1.5">
            <Search className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the list was opened to be typed into
              autoFocus
              type="text"
              value={query}
              aria-label="Search agents by name"
              placeholder="Search name…"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                const first = visible.find((row) => row.coversDestination);
                if (first) pick(first.id);
              }}
              className="text-foreground placeholder:text-muted-foreground h-7 w-full bg-transparent text-sm outline-none"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="text-muted-foreground px-2 py-3 text-center text-[13px]">No matches</p>
            ) : (
              visible.map((row) => {
                const selected = row.id === (value ?? currentAgentId);
                return (
                  <button
                    key={row.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    disabled={!row.coversDestination}
                    onClick={() => pick(row.id)}
                    title={
                      row.coversDestination
                        ? undefined
                        : `${row.fullName}'s sales coverage does not include ${destinationName}.`
                    }
                    className={cn(
                      'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                      row.coversDestination
                        ? 'text-foreground hover:bg-muted'
                        : 'text-muted-foreground cursor-not-allowed opacity-60',
                    )}
                  >
                    <Check
                      className="h-3.5 w-3.5 shrink-0"
                      aria-hidden
                      style={{ visibility: selected ? 'visible' : 'hidden' }}
                    />
                    <span className="min-w-0 flex-1 truncate">{row.fullName}</span>
                    <span className="text-muted-foreground shrink-0 text-[11px]">
                      {row.role === 'SALES_LEAD' ? 'Team lead' : 'Agent'}
                      {row.id === currentAgentId ? ' · current' : ''}
                    </span>
                    {!row.coversDestination && (
                      <span className="shrink-0 text-[11px]">Not covered</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
